import { X, ChevronDown, BarChart3, Target, TrendingUp } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Language, dictionary } from "../../../utils/dictionary";
import { getMetaOAuthUrl, getMetaIntegration } from "../../../services/meta/metaService";
const META_LOGO = "https://fluxi.s3.amazonaws.com/dev/assets/meta-logo.svg";
const FLUXI_LOGO = "https://fluxi.s3.amazonaws.com/prod/assets/fluxi/fluxi-logo.svg";

interface ModalMetaIntegrationProps {
  setModalOpen: (open: boolean) => void;
  handleOAuthComplete: () => void;
  loading: boolean;
  language: Language;
}

const ModalMetaIntegration = ({
  setModalOpen,
  handleOAuthComplete,
  loading,
  language,
}: ModalMetaIntegrationProps) => {
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  const handleConnect = async () => {
    setError(null);
    setConnecting(true);
    try {
      const response = await getMetaOAuthUrl();
      const popup = window.open(
        response.authorizationUrl,
        "meta-oauth",
        "width=600,height=700,scrollbars=yes"
      );

      intervalRef.current = setInterval(async () => {
        if (popup?.closed) {
          if (intervalRef.current) clearInterval(intervalRef.current);
          intervalRef.current = null;
          try {
            const integration = await getMetaIntegration();
            if (integration?.is_active) {
              setConnecting(false);
              handleOAuthComplete();
              return;
            }
          } catch {
            // Integration not found or error
          }
          setConnecting(false);
          setError(dictionary["meta-connect-error"][language]);
        }
      }, 500);
    } catch (err: any) {
      console.error("Meta OAuth failed:", err);
      setError(err?.message || dictionary["meta-connect-error"][language]);
      setConnecting(false);
    }
  };

  const isLoading = loading || connecting;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-[850px] overflow-hidden flex flex-col md:flex-row relative">
        <button
          onClick={() => setModalOpen(false)}
          className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 z-10 md:hidden"
        >
          <X size={24} />
        </button>

        {/* Left Side - Content */}
        <div className="flex-1 p-6 md:p-8 flex flex-col">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-12 h-12 flex items-center justify-center shrink-0">
              <img src={META_LOGO} alt="Meta" className="w-full h-full object-contain" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 leading-tight">
              {dictionary["meta-connect-title"][language]}
            </h2>
          </div>

          <p className="text-gray-600 mb-6 text-sm leading-relaxed">
            {dictionary["meta-connect-description"][language]}
          </p>

          <div className="space-y-3 mb-6">
            <div className="flex items-start gap-3">
              <BarChart3 className="w-5 h-5 text-fluxi mt-0.5 shrink-0" />
              <span className="text-sm text-gray-700">
                {dictionary["meta-feature-metrics"]?.[language] || "Métricas de campañas en tiempo real"}
              </span>
            </div>
            <div className="flex items-start gap-3">
              <Target className="w-5 h-5 text-fluxi mt-0.5 shrink-0" />
              <span className="text-sm text-gray-700">
                {dictionary["meta-feature-audiences"]?.[language] || "Gestión de audiencias y segmentación"}
              </span>
            </div>
            <div className="flex items-start gap-3">
              <TrendingUp className="w-5 h-5 text-fluxi mt-0.5 shrink-0" />
              <span className="text-sm text-gray-700">
                {dictionary["meta-feature-performance"]?.[language] || "Análisis de rendimiento de anuncios"}
              </span>
            </div>
          </div>

          {error && (
            <div className="w-full mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="mt-auto pt-4 border-t border-gray-100">
            <div className="flex gap-3">
              <button
                className="flex-1 bg-fluxi hover:bg-fluxi-dark text-white font-semibold py-2.5 px-4 rounded-lg transition-colors text-sm shadow-sm flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleConnect}
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  dictionary["meta-connect-button"][language]
                )}
              </button>
              <button
                className="flex-1 bg-white hover:bg-gray-50 text-gray-700 font-medium py-2.5 px-4 rounded-lg border border-gray-200 transition-colors text-sm"
                onClick={() => setModalOpen(false)}
              >
                {dictionary["cancel"][language]}
              </button>
            </div>
          </div>
        </div>

        {/* Right Side - Visual */}
        <div className="hidden md:flex w-[200px] bg-gradient-to-b from-[#eff6ff] to-white relative items-center justify-center py-6 px-4 border-l border-gray-100 shrink-0">
          <button
            onClick={() => setModalOpen(false)}
            className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 z-10 transition-colors"
          >
            <X size={18} />
          </button>

          <div className="flex flex-col items-center justify-center gap-8 w-full h-full">
            <div className="bg-white rounded-[24px] shadow-[0_4px_20px_rgba(67,104,229,0.15)] border border-blue-100 p-5 w-24 h-24 flex items-center justify-center z-10 relative">
              <img src={FLUXI_LOGO} alt="Fluxi" className="w-full h-auto object-contain" />
            </div>

            <div className="flex flex-col items-center gap-1 text-blue-300">
              <ChevronDown strokeWidth={3} size={24} className="animate-[metaFade_1.5s_infinite_0ms]" />
              <ChevronDown strokeWidth={3} size={24} className="animate-[metaFade_1.5s_infinite_200ms]" />
              <ChevronDown strokeWidth={3} size={24} className="animate-[metaFade_1.5s_infinite_400ms]" />
            </div>

            <div className="bg-white rounded-[24px] shadow-sm border border-gray-200 p-5 w-24 h-24 flex items-center justify-center z-10 relative">
              <img src={META_LOGO} alt="Meta" className="w-16 h-auto object-contain" />
            </div>

            <style>{`
              @keyframes metaFade {
                0%, 100% { opacity: 0.3; transform: translateY(0); }
                50% { opacity: 1; transform: translateY(3px); }
              }
            `}</style>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModalMetaIntegration;
